"use client";

import React, { useState, useMemo, useEffect, useRef } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "~/i18n/navigation";
import {
  Sparkles,
  MapPin,
  Clock,
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  Dice5,
  Zap,
  Copy,
  Globe,
  Eye,
  EyeOff,
  ArrowRight,
  Radio,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";
import type * as LeafletTypes from "leaflet";

interface SpawnTemplate {
  key: string;
  name: string;
  id: number;
  cp: number;
  iv: string;
  ivPct: number;
  level: number;
  despawn: string;
  normalImg: string;
  shinyImg: string;
  latOffset: number;
  lngOffset: number;
}

interface CityHotspot {
  id: string;
  name: string;
  areaLabel: string;
  flag: string;
  center: [number, number];
  zoom: number;
  polygonOffsets: [number, number][];
}

const CITY_HOTSPOTS: CityHotspot[] = [
  {
    id: "london",
    name: "London",
    areaLabel: "Hyde Park Scanner #1",
    flag: "🇬🇧",
    center: [51.5073, -0.1657],
    zoom: 15,
    polygonOffsets: [
      [0.0058, -0.0115],
      [0.0062, 0.0095],
      [-0.0048, 0.0112],
      [-0.0056, -0.0098],
    ],
  },
  {
    id: "newyork",
    name: "New York",
    areaLabel: "Central Park South",
    flag: "🇺🇸",
    center: [40.7712, -73.9742],
    zoom: 15,
    polygonOffsets: [
      [0.006, -0.0095],
      [0.0055, 0.0105],
      [-0.0052, 0.009],
      [-0.0058, -0.0102],
    ],
  },
  {
    id: "tokyo",
    name: "Tokyo",
    areaLabel: "Yoyogi & Shibuya",
    flag: "🇯🇵",
    center: [35.6712, 139.6965],
    zoom: 15,
    polygonOffsets: [
      [0.0056, -0.01],
      [0.0058, 0.01],
      [-0.0054, 0.0095],
      [-0.0052, -0.0105],
    ],
  },
  {
    id: "sydney",
    name: "Sydney",
    areaLabel: "Circular Quay Radar",
    flag: "🇦🇺",
    center: [-33.8615, 151.2128],
    zoom: 15,
    polygonOffsets: [
      [0.0054, -0.0095],
      [0.0056, 0.0098],
      [-0.0052, 0.0102],
      [-0.0055, -0.0092],
    ],
  },
];

const SPAWN_TEMPLATES: SpawnTemplate[] = [
  {
    key: "charizard",
    name: "Charizard",
    id: 6,
    cp: 2641,
    iv: "15/15/15",
    ivPct: 100,
    level: 33,
    despawn: "18m 42s",
    normalImg: "/pokemon/charizard.icon.webp",
    shinyImg: "/pokemon/charizard.shiny.icon.webp",
    latOffset: 0.0022,
    lngOffset: -0.0038,
  },
  {
    key: "rayquaza",
    name: "Rayquaza",
    id: 384,
    cp: 3412,
    iv: "15/15/14",
    ivPct: 98,
    level: 32,
    despawn: "14m 19s",
    normalImg: "/pokemon/rayquaza.icon.webp",
    shinyImg: "/pokemon/rayquaza.shiny.icon.webp",
    latOffset: -0.0014,
    lngOffset: 0.0035,
  },
  {
    key: "lucario",
    name: "Lucario",
    id: 448,
    cp: 2385,
    iv: "15/14/15",
    ivPct: 98,
    level: 31,
    despawn: "16m 05s",
    normalImg: "/pokemon/lucario.icon.webp",
    shinyImg: "/pokemon/lucario.shiny.icon.webp",
    latOffset: 0.0031,
    lngOffset: 0.0042,
  },
  {
    key: "gengar",
    name: "Gengar",
    id: 94,
    cp: 2490,
    iv: "15/15/15",
    ivPct: 100,
    level: 30,
    despawn: "11m 54s",
    normalImg: "/pokemon/gengar.icon.webp",
    shinyImg: "/pokemon/gengar.shiny.icon.webp",
    latOffset: -0.0029,
    lngOffset: -0.0045,
  },
  {
    key: "gyarados",
    name: "Gyarados",
    id: 130,
    cp: 2915,
    iv: "14/15/15",
    ivPct: 98,
    level: 31,
    despawn: "19m 10s",
    normalImg: "/pokemon/gyarados.icon.webp",
    shinyImg: "/pokemon/gyarados.shiny.icon.webp",
    latOffset: 0.0005,
    lngOffset: -0.0068,
  },
  {
    key: "metagross",
    name: "Metagross",
    id: 376,
    cp: 3254,
    iv: "15/15/13",
    ivPct: 96,
    level: 31,
    despawn: "09m 38s",
    normalImg: "/pokemon/metagross.icon.webp",
    shinyImg: "/pokemon/metagross.shiny.icon.webp",
    latOffset: -0.0034,
    lngOffset: 0.0008,
  },
  {
    key: "dragonite",
    name: "Dragonite",
    id: 149,
    cp: 3380,
    iv: "15/14/14",
    ivPct: 96,
    level: 32,
    despawn: "15m 27s",
    normalImg: "/pokemon/dragonite.icon.webp",
    shinyImg: "/pokemon/dragonite.shiny.icon.webp",
    latOffset: 0.0036,
    lngOffset: -0.0002,
  },
  {
    key: "bagon",
    name: "Bagon",
    id: 371,
    cp: 992,
    iv: "15/15/15",
    ivPct: 100,
    level: 30,
    despawn: "17m 50s",
    normalImg: "/pokemon/bagon.icon.webp",
    shinyImg: "/pokemon/bagon.shiny.icon.webp",
    latOffset: -0.0004,
    lngOffset: 0.0069,
  },
];

const SAMPLE_TRAINERS = [
  { label: "Trainer #1", code: "8492 1047 3921", team: "Mystic • Lv 48" },
  { label: "Trainer #2", code: "3109 5824 7710", team: "Valor • Lv 50" },
  { label: "Trainer #3", code: "6641 9028 1539", team: "Instinct • Lv 44" },
];

export default function ShinyBugDemo() {
  const t = useTranslations("ShinyBug");

  const [friendCode, setFriendCode] = useState("8492 1047 3921");
  const [selectedCityId, setSelectedCityId] = useState<string>("london");
  const [personalRadarOn, setPersonalRadarOn] = useState<boolean>(true);
  const [selectedSpawnKey, setSelectedSpawnKey] = useState<string>("charizard");

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletTypes.Map | null>(null);
  const leafletModRef = useRef<typeof LeafletTypes | null>(null);
  const markersLayerRef = useRef<LeafletTypes.LayerGroup | null>(null);
  const polygonLayerRef = useRef<LeafletTypes.Polygon | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const currentCity = useMemo(
    () =>
      CITY_HOTSPOTS.find((c) => c.id === selectedCityId) ?? CITY_HOTSPOTS[0]!,
    [selectedCityId],
  );

  // Derive deterministic Player ID & which 3 spawns on the map roll Shiny for this Trainer Code
  const { derivedPlayerId, shinyKeysForTrainer } = useMemo(() => {
    const clean = friendCode.replace(/\D/g, "") || "849210473921";
    let hash = 2166136261;
    for (let i = 0; i < clean.length; i++) {
      hash ^= clean.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    const absHash = Math.abs(hash);
    const hex = absHash.toString(16).padStart(8, "0");
    const playerId = `${hex}d1f44a839e2389104f${clean.slice(-4).padStart(4, "0")}`;

    // Deterministically pick 3 distinct spawns to be shiny for this trainer code
    const total = SPAWN_TEMPLATES.length;
    const idx1 = absHash % total;
    const idx2 = (absHash + 3) % total;
    const idx3 = (absHash + 6) % total;
    const shinySet = new Set<string>([
      SPAWN_TEMPLATES[idx1]!.key,
      SPAWN_TEMPLATES[idx2]!.key,
      SPAWN_TEMPLATES[idx3]!.key,
    ]);

    return {
      derivedPlayerId: playerId,
      shinyKeysForTrainer: shinySet,
    };
  }, [friendCode]);

  const spawnsWithCoordinates = useMemo(() => {
    return SPAWN_TEMPLATES.map((spawn) => {
      const lat = Number((currentCity.center[0] + spawn.latOffset).toFixed(5));
      const lng = Number((currentCity.center[1] + spawn.lngOffset).toFixed(5));
      const isShinyForTrainer =
        personalRadarOn && shinyKeysForTrainer.has(spawn.key);
      return {
        ...spawn,
        lat,
        lng,
        isShinyForTrainer,
      };
    });
  }, [currentCity, personalRadarOn, shinyKeysForTrainer]);

  const currentSpawn = useMemo(() => {
    return (
      spawnsWithCoordinates.find((s) => s.key === selectedSpawnKey) ??
      spawnsWithCoordinates[0]!
    );
  }, [spawnsWithCoordinates, selectedSpawnKey]);

  // When trainer code changes, auto-select one of their shiny spawns so the user immediately sees the magic
  function selectFirstShinyForCode(newCode: string) {
    const clean = newCode.replace(/\D/g, "") || "849210473921";
    let hash = 2166136261;
    for (let i = 0; i < clean.length; i++) {
      hash ^= clean.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    const idx1 = Math.abs(hash) % SPAWN_TEMPLATES.length;
    setSelectedSpawnKey(SPAWN_TEMPLATES[idx1]!.key);
  }

  function handleRandomCode() {
    const p1 = Math.floor(1000 + Math.random() * 9000);
    const p2 = Math.floor(1000 + Math.random() * 9000);
    const p3 = Math.floor(1000 + Math.random() * 9000);
    const nextCode = `${p1} ${p2} ${p3}`;
    setFriendCode(nextCode);
    setPersonalRadarOn(true);
    selectFirstShinyForCode(nextCode);
  }

  // Initialize Leaflet Map on client
  useEffect(() => {
    let isMounted = true;
    if (!mapContainerRef.current || mapRef.current) return;

    void import("leaflet").then((L) => {
      if (!isMounted || !mapContainerRef.current || mapRef.current) return;
      leafletModRef.current = L;

      const map = L.map(mapContainerRef.current, {
        center: currentCity.center,
        zoom: currentCity.zoom,
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      markersLayerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
      setMapReady(true);
    });

    return () => {
      isMounted = false;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map center & geofence polygon when city changes
  useEffect(() => {
    const L = leafletModRef.current;
    const map = mapRef.current;
    if (!L || !map || !mapReady) return;

    map.setView(currentCity.center, currentCity.zoom, { animate: true });

    if (polygonLayerRef.current) {
      map.removeLayer(polygonLayerRef.current);
    }

    const polyCoords: [number, number][] = currentCity.polygonOffsets.map(
      ([dLat, dLng]) => [
        currentCity.center[0] + dLat,
        currentCity.center[1] + dLng,
      ],
    );

    polygonLayerRef.current = L.polygon(polyCoords, {
      color: "#ec4899",
      weight: 2.5,
      dashArray: "6, 6",
      fillColor: "#ec4899",
      fillOpacity: 0.08,
      interactive: false,
    }).addTo(map);
  }, [currentCity, mapReady]);

  // Render Pokemon Markers on the Map whenever spawns, trainer code, radar toggle, or selection changes
  useEffect(() => {
    const L = leafletModRef.current;
    const map = mapRef.current;
    const layerGroup = markersLayerRef.current;
    if (!L || !map || !layerGroup || !mapReady) return;

    layerGroup.clearLayers();

    spawnsWithCoordinates.forEach((spawn) => {
      const isSelected = spawn.key === selectedSpawnKey;
      const isShiny = spawn.isShinyForTrainer;
      const imgUrl = isShiny ? spawn.shinyImg : spawn.normalImg;

      const ringStyle = isShiny
        ? "border: 2.5px solid #f59e0b; background: radial-gradient(circle, rgba(254,243,199,0.98) 0%, rgba(253,230,138,0.92) 100%); box-shadow: 0 0 0 4px rgba(245,158,11,0.32), 0 8px 20px rgba(0,0,0,0.35);"
        : "border: 2px solid #cbd5e1; background: rgba(255,255,255,0.95); box-shadow: 0 4px 12px rgba(15,23,42,0.22);";

      const selectedScale = isSelected
        ? "transform: scale(1.18); z-index: 50;"
        : "transform: scale(1);";

      const badgeHtml = isShiny
        ? `<div style="position:absolute; -top:9px; left:50%; transform:translate(-50%, -65%); background:linear-gradient(90deg,#f59e0b,#ec4899); color:#fff; font-size:9px; font-weight:900; padding:1px 6px; border-radius:9999px; white-space:nowrap; box-shadow:0 2px 6px rgba(236,72,153,0.5); letter-spacing:0.03em;">✨ SHINY</div>`
        : `<div style="position:absolute; -top:7px; left:50%; transform:translate(-50%, -55%); background:#0f172a; color:#34d399; font-size:9px; font-weight:800; padding:1px 5px; border-radius:9999px; white-space:nowrap;">${spawn.ivPct}% IV</div>`;

      const html = `
        <div style="position:relative; width:54px; height:54px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s ease; ${selectedScale}">
          ${badgeHtml}
          <div style="width:48px; height:48px; border-radius:9999px; display:flex; align-items:center; justify-content:center; ${ringStyle}">
            <img src="${imgUrl}" alt="${spawn.name}" style="width:40px; height:40px; object-fit:contain;" />
          </div>
          ${
            isSelected
              ? `<div style="position:absolute; bottom:-6px; left:50%; transform:translateX(-50%); width:8px; height:8px; background:#ec4899; border:2px solid #fff; border-radius:9999px;"></div>`
              : ""
          }
        </div>
      `;

      const icon = L.divIcon({
        html,
        className: "pory-demo-marker",
        iconSize: [54, 54],
        iconAnchor: [27, 27],
      });

      const marker = L.marker([spawn.lat, spawn.lng], {
        icon,
        zIndexOffset: isSelected ? 1000 : isShiny ? 500 : 100,
      });

      marker.on("click", () => {
        setSelectedSpawnKey(spawn.key);
      });

      marker.addTo(layerGroup);
    });
  }, [spawnsWithCoordinates, selectedSpawnKey, mapReady]);

  return (
    <div className="relative overflow-hidden rounded-3xl border-2 border-pory-pink-200/80 bg-linear-to-b from-white via-pory-pink-50/20 to-pory-blue-50/30 p-5 shadow-2xl sm:p-8 lg:p-10">
      {/* Decorative background glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-pory-pink-300/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-pory-blue-300/20 blur-3xl" />

      {/* Header */}
      <div className="relative z-10 mb-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-1.5 text-xs font-black tracking-wider text-amber-800 uppercase shadow-xs backdrop-blur-sm">
          <Sparkles className="h-3.5 w-3.5 text-amber-600" />
          <span>{t("demoTag")}</span>
        </div>
        <h2 className="mt-3 text-2xl font-black tracking-tight text-pory-blue-950 sm:text-3xl lg:text-4xl">
          {t("demoHeading")}
        </h2>
        <p className="mx-auto mt-2 max-w-3xl text-sm leading-relaxed text-pory-blue-800/80 sm:text-base">
          {t("demoSubtitle")}
        </p>
      </div>

      {/* Top Interactive Controls Bar: Trainer Code + Preset Trainers + Resolved Map ID */}
      <div className="relative z-10 mb-6 grid grid-cols-1 gap-4 rounded-2xl border border-pory-blue-100 bg-white/95 p-4 shadow-md backdrop-blur-sm lg:grid-cols-12 lg:items-center">
        {/* Step 1: Enter or Switch Trainer Code */}
        <div className="lg:col-span-5">
          <div className="mb-1.5 flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-xs font-black tracking-wider text-pory-blue-950 uppercase">
              <Users className="h-3.5 w-3.5 text-pory-pink-500" />
              <span>{t("demoFriendCodeLabel")}</span>
            </label>
            <button
              type="button"
              onClick={handleRandomCode}
              className="inline-flex items-center gap-1 rounded-full bg-pory-pink-50 px-2.5 py-0.5 text-xs font-bold text-pory-pink-600 transition hover:bg-pory-pink-100"
            >
              <Dice5 className="h-3.5 w-3.5" />
              <span>{t("demoRandomBtn")}</span>
            </button>
          </div>

          <div className="relative">
            <input
              type="text"
              value={friendCode}
              onChange={(e) => {
                setFriendCode(e.target.value);
                setPersonalRadarOn(true);
              }}
              placeholder={t("demoFriendCodePlaceholder")}
              className="w-full rounded-xl border border-pory-blue-200 bg-pory-blue-50/40 px-3.5 py-2.5 font-mono text-base font-bold tracking-wider text-pory-blue-950 transition outline-none focus:border-pory-pink-400 focus:bg-white focus:ring-2 focus:ring-pory-pink-200"
            />
            <div className="pointer-events-none absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>{t("demoSafeBadge")}</span>
            </div>
          </div>

          {/* Preset Trainer Switcher Pills */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-500 mr-1">
              {t("demoCompareTrainers")}
            </span>
            {SAMPLE_TRAINERS.map((tr) => {
              const active = friendCode.trim() === tr.code;
              return (
                <button
                  key={tr.code}
                  type="button"
                  onClick={() => {
                    setFriendCode(tr.code);
                    setPersonalRadarOn(true);
                    selectFirstShinyForCode(tr.code);
                  }}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition ${
                    active
                      ? "bg-pory-blue-950 text-white shadow-xs"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {tr.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Derived In-Game Player ID for Live Map */}
        <div className="lg:col-span-4 rounded-xl border border-amber-200/80 bg-amber-50/50 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-black tracking-wider text-amber-950 uppercase">
              {t("demoPlayerIdLabel")}
            </span>
            <span className="rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-extrabold text-amber-900 uppercase">
              {t("demoMapSettingBadge")}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              readOnly
              value={derivedPlayerId}
              onClick={(e) => {
                (e.target as HTMLInputElement).select();
                void navigator.clipboard.writeText(derivedPlayerId);
                toast.success(t("demoCopiedToast"));
              }}
              className="w-full cursor-pointer rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 font-mono text-xs font-bold text-slate-900 outline-none select-all"
            />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(derivedPlayerId);
                toast.success(t("demoCopiedToast"));
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-bold text-white shadow-xs transition hover:bg-amber-600"
            >
              <Copy className="h-3.5 w-3.5" />
              <span>{t("demoCopyBtn")}</span>
            </button>
          </div>
          <p className="mt-1.5 text-[11px] leading-tight text-amber-900/80">
            {t("demoPlayerIdHint")}
          </p>
        </div>

        {/* Step 3: Toggle Personal Shiny Radar on Map */}
        <div className="lg:col-span-3 flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-900 p-3.5 text-white">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300">
              {t("demoRadarStatusLabel")}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                personalRadarOn
                  ? "bg-amber-400/20 text-amber-300 border border-amber-400/40"
                  : "bg-slate-700 text-slate-300"
              }`}
            >
              {personalRadarOn ? "✨ ACTIVE" : "OFF"}
            </span>
          </div>

          <p className="my-1.5 text-xs font-bold text-white">
            {personalRadarOn
              ? t("demoShiniesFoundCount", { count: shinyKeysForTrainer.size })
              : t("demoStandardMapMode")}
          </p>

          <button
            type="button"
            onClick={() => setPersonalRadarOn((prev) => !prev)}
            className={`mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition ${
              personalRadarOn
                ? "bg-linear-to-r from-amber-400 to-pory-pink-500 text-slate-950 shadow-md"
                : "bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
            }`}
          >
            {personalRadarOn ? (
              <>
                <Eye className="h-3.5 w-3.5" />
                <span>{t("demoTogglePersonalOn")}</span>
              </>
            ) : (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                <span>{t("demoTogglePersonalOff")}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Split View: Interactive World/City Map (Left 7 cols) + Live Discord Alert & CTA (Right 5 cols) */}
      <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Live Map Experience */}
        <div className="flex flex-col overflow-hidden rounded-2xl border-2 border-slate-200 bg-slate-900 shadow-xl lg:col-span-7">
          {/* Map Top Bar: City Hotspot Selector */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 bg-slate-950 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-pory-pink-400" />
              <span className="text-xs font-black tracking-wider uppercase">
                {t("demoLiveMapHeader")}
              </span>
              <span className="hidden rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-400 border border-emerald-500/30 sm:inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {currentCity.areaLabel}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1">
              {CITY_HOTSPOTS.map((city) => {
                const isSelected = city.id === selectedCityId;
                return (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => setSelectedCityId(city.id)}
                    className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${
                      isSelected
                        ? "bg-pory-pink-500 text-white shadow-sm"
                        : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    }`}
                  >
                    {city.flag} {city.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Interactive Leaflet Map Canvas */}
          <div className="relative h-[380px] w-full bg-slate-800 sm:h-[420px]">
            <div ref={mapContainerRef} className="h-full w-full z-0" />

            {/* Map Floating Legend Overlay */}
            <div className="pointer-events-none absolute bottom-3 left-3 z-[400] flex flex-wrap items-center gap-2 rounded-xl border border-slate-700/80 bg-slate-950/90 px-3 py-2 text-[11px] font-bold text-white shadow-lg backdrop-blur-md">
              <span className="inline-flex items-center gap-1.5 text-amber-300">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-amber-400/40" />
                {t("demoLegendPersonalShiny")}
              </span>
              <span className="text-slate-600">•</span>
              <span className="inline-flex items-center gap-1.5 text-slate-300">
                <span className="h-2.5 w-2.5 rounded-full bg-white" />
                {t("demoLegendNormalSpawn")}
              </span>
              <span className="text-slate-600">•</span>
              <span className="inline-flex items-center gap-1 text-pory-pink-400">
                <Radio className="h-3 w-3" />
                {t("demoLegendGeofence")}
              </span>
            </div>
          </div>

          {/* Quick Spawn Selector Bar Under Map */}
          <div className="border-t border-slate-800 bg-slate-950 p-3">
            <div className="mb-2 flex items-center justify-between text-[11px] text-slate-400">
              <span className="font-bold uppercase tracking-wider text-slate-300">
                {t("demoClickSpawnPrompt")}
              </span>
              <span className="text-amber-300 font-bold">
                {t("demoTrainerRollHint")}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
              {spawnsWithCoordinates.map((spawn) => {
                const isSelected = spawn.key === selectedSpawnKey;
                const isShiny = spawn.isShinyForTrainer;
                return (
                  <button
                    key={spawn.key}
                    type="button"
                    onClick={() => setSelectedSpawnKey(spawn.key)}
                    className={`relative flex flex-col items-center justify-center rounded-xl p-1.5 transition-all ${
                      isSelected
                        ? "border-2 border-pory-pink-500 bg-slate-800 shadow-md"
                        : isShiny
                          ? "border border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/20"
                          : "border border-slate-800 bg-slate-900/80 opacity-75 hover:opacity-100"
                    }`}
                  >
                    {isShiny && (
                      <span className="absolute -top-1.5 right-1 rounded-full bg-amber-400 px-1 text-[8px] font-black text-slate-950">
                        ✨
                      </span>
                    )}
                    <div className="relative h-9 w-9">
                      <Image
                        src={isShiny ? spawn.shinyImg : spawn.normalImg}
                        alt={spawn.name}
                        fill
                        className="object-contain"
                        sizes="36px"
                      />
                    </div>
                    <span className="mt-0.5 max-w-full truncate text-[10px] font-bold text-white">
                      {spawn.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Selected Map Spawn Inspection + Live Discord DM + Conversion CTA */}
        <div className="flex flex-col justify-between rounded-2xl border-2 border-pory-blue-100 bg-slate-900 p-5 text-white shadow-2xl lg:col-span-5">
          <div>
            {/* Discord DM Header */}
            <div className="mb-4 flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-linear-to-tr from-pory-pink-500 to-indigo-600 text-sm font-black shadow-inner">
                  P
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-100">
                      {t("demoAlertPreviewFrom")}
                    </span>
                    <span className="rounded-md bg-[#5865F2] px-1.5 py-0.5 text-[10px] font-extrabold text-white uppercase">
                      BOT
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {currentCity.flag} {currentCity.name} •{" "}
                    {t("demoDirectMessageLabel")}
                  </span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-bold text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live Feed
              </span>
            </div>

            {/* Selected Spawn Status Card */}
            {currentSpawn.isShinyForTrainer ? (
              <div className="space-y-3.5">
                <div className="flex items-center gap-3 rounded-xl border border-amber-400/40 bg-linear-to-r from-amber-500/20 via-rose-500/20 to-purple-500/20 p-3 backdrop-blur-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/40 bg-amber-400/20 text-xl">
                    ✨
                  </div>
                  <div>
                    <h4 className="text-xs font-black tracking-wide text-amber-300 uppercase">
                      {t("demoResultShinyTitle")}
                    </h4>
                    <p className="text-xs leading-snug text-slate-200">
                      {t("demoResultShinyDesc")}
                    </p>
                  </div>
                </div>

                {/* Discord Embed Preview */}
                <div className="rounded-xl border border-slate-800 border-l-4 border-l-amber-400 bg-[#2b2d31] p-4 text-slate-200 shadow-md">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-1.5">
                      <span className="inline-block rounded bg-amber-400/20 px-2 py-0.5 text-[10px] font-black tracking-wider text-amber-300 uppercase">
                        ✨ {t("demoPersonalShinyRollBadge")}
                      </span>
                      <h5 className="text-lg font-black text-white">
                        {currentSpawn.name} (Shiny) • CP {currentSpawn.cp}
                      </h5>
                      <p className="text-xs font-medium text-slate-300">
                        Lv {currentSpawn.level} • IV:{" "}
                        <span className="font-bold text-emerald-400">
                          {currentSpawn.iv} ({currentSpawn.ivPct}%)
                        </span>
                      </p>

                      <div className="grid grid-cols-2 gap-2 pt-1.5 text-xs">
                        <div className="rounded-lg border border-slate-700/60 bg-slate-800/80 p-2">
                          <span className="block text-[10px] font-bold text-slate-400 uppercase">
                            {t("demoAlertPreviewCoordinates")}
                          </span>
                          <span className="font-mono text-[11px] font-bold text-slate-200">
                            {currentSpawn.lat}, {currentSpawn.lng}
                          </span>
                        </div>
                        <div className="rounded-lg border border-slate-700/60 bg-slate-800/80 p-2">
                          <span className="block text-[10px] font-bold text-slate-400 uppercase">
                            {t("demoAlertPreviewDespawn")}
                          </span>
                          <span className="flex items-center gap-1 font-bold text-rose-400">
                            <Clock className="h-3 w-3" />
                            {currentSpawn.despawn}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-amber-400/40 bg-linear-to-tr from-slate-800 to-slate-700 p-2">
                      <div className="absolute inset-0 rounded-2xl bg-amber-400/15 animate-pulse" />
                      <Image
                        src={currentSpawn.shinyImg}
                        alt={`${currentSpawn.name} Shiny`}
                        width={72}
                        height={72}
                        className="relative z-10 object-contain drop-shadow-[0_4px_12px_rgba(251,191,36,0.45)]"
                      />
                      <span className="absolute top-1 right-1 text-xs">✨</span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-800 pt-2.5">
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#4e5058] px-2.5 py-1 text-[11px] font-bold text-white">
                      <MapPin className="h-3 w-3 text-rose-400" />
                      <span>{t("demoAlertPreviewMaps")}</span>
                      <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#4e5058] px-2.5 py-1 text-[11px] font-bold text-white">
                      <Zap className="h-3 w-3 text-amber-400" />
                      <span>Trainer: {friendCode}</span>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3.5">
                <div className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800/80 p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-700 font-bold text-slate-300">
                    ⚪
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-200 uppercase">
                      {t("demoResultNormalTitle")}
                    </h4>
                    <p className="text-xs leading-snug text-slate-400">
                      {t("demoResultNormalDesc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-[#2b2d31] p-4 text-slate-300">
                  <div className="space-y-1 pr-2">
                    <span className="inline-block rounded bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-emerald-400">
                      Global IV: {currentSpawn.iv} ({currentSpawn.ivPct}%)
                    </span>
                    <h5 className="text-base font-bold text-white">
                      {currentSpawn.name} • CP {currentSpawn.cp}
                    </h5>
                    <p className="text-xs text-slate-400">
                      {t("demoNormalFilteredNote")}
                    </p>
                  </div>
                  <div className="relative h-16 w-16 shrink-0 opacity-75">
                    <Image
                      src={currentSpawn.normalImg}
                      alt={currentSpawn.name}
                      fill
                      className="object-contain"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Direct Conversion CTA Box inside the Demo */}
          <div className="mt-5 rounded-xl border border-pory-pink-500/40 bg-linear-to-br from-pory-pink-500/15 via-slate-800/90 to-indigo-500/15 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-black text-amber-300">
                {t("demoCtaBoxTitle")}
              </span>
              <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t("demoCtaBoxSub")}
              </span>
            </div>

            <Link
              href="/sign-up"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-linear-to-r from-pory-pink-500 to-rose-500 px-4 py-3 text-sm font-black text-white shadow-lg shadow-pory-pink-500/30 transition hover:scale-[1.01] hover:shadow-xl"
            >
              <Sparkles className="h-4 w-4" />
              <span>{t("demoCtaPrimaryBtn")}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>

            <div className="mt-2.5 flex items-center justify-between text-[11px] text-slate-300">
              <Link
                href="/dashboard"
                className="font-bold text-pory-pink-300 underline-offset-2 hover:underline"
              >
                {t("demoCtaDashboardLink")}
              </Link>
              <a
                href="#pricing"
                className="font-bold text-slate-300 underline-offset-2 hover:text-white hover:underline"
              >
                {t("demoCtaPricingLink")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
